import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Send } from "lucide-react";

import Reveal from "@/components/public/Reveal";
import { publicApi } from "@/lib/api";
import { COMPANY } from "@/lib/company";

const PROJECT_TYPES = ["Residential Design", "BIM Production", "General Inquiry"];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: "Residential Design",
    message: "",
    privacy_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [page, setPage] = useState(null);

  useEffect(() => {
    publicApi.track({ event_type: "page_view", page: "/contact" }).catch(() => {});
    publicApi.getPage("contact").then(setPage).catch(() => {});
  }, []);

  const hero = page?.content?.hero || {
    headline: "Get in Touch",
    subheadline: "Tell us about your project or BIM production needs.",
    background:
      "/assets/minga/projects/waters-residence/02-dining.jpg",
  };
  const card = page?.content?.card || {
    headline: "Let’s Start a Project",
    paragraph:
      "Send us an email or use the web form to get in touch. We endeavor to answer all enquiries within 24 hours on business days.",
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!form.privacy_accepted) {
      toast.error("Please acknowledge the Privacy Policy before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.submitLead({ ...form, source_page: "/contact" });
      setSubmitted(true);
      toast.success("Thank you — we’ll be in touch shortly.");
      setForm({
        name: "",
        email: "",
        project_type: "Residential Design",
        message: "",
        privacy_accepted: false,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="relative h-[70svh] min-h-[480px] w-full overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${hero.background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-black/70 via-ink-black/40 to-ink-black" />
        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-14 md:pb-20">
          <p className="eyebrow">{hero.eyebrow || "[ Contact ]"}</p>
          <h1 className="hero-headline mt-6 max-w-4xl">{hero.headline}</h1>
          <p className="body-text mt-6 max-w-2xl md:text-lg text-brand-white/85">
            {hero.subheadline}
          </p>
        </div>
      </section>

      <section className="section-pad max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">[ Reach Out ]</p>
            <h2 className="section-headline mt-4">{card.headline}</h2>
            <p className="body-text mt-6 max-w-md">{card.paragraph}</p>
            <a
              href={`mailto:${COMPANY.email}`}
              className="group mt-8 inline-flex items-center gap-3 border-y border-white/15 py-4 pr-4 hover:border-brand-yellow transition-colors"
              data-testid="contact-email"
            >
              <Mail className="h-5 w-5 text-brand-yellow" />
              <span className="text-lg md:text-xl font-medium tracking-tight">
                {COMPANY.email}
              </span>
              <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <div className="mt-8 text-xs uppercase tracking-[0.24em] text-brand-gray">
              Seattle, WA — Quito, Ecuador
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={80}>
            <form
              onSubmit={submit}
              className="surface p-6 md:p-10 space-y-5"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <FormField label="Name" htmlFor="name">
                  <input
                    id="name"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="form-input"
                    data-testid="contact-name"
                  />
                </FormField>
                <FormField label="Email" htmlFor="email">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@email.com"
                    className="form-input"
                    data-testid="contact-email-input"
                  />
                </FormField>
              </div>
              <FormField label="Project Type" htmlFor="project_type">
                <select
                  id="project_type"
                  name="project_type"
                  value={form.project_type}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="contact-project-type"
                >
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Message" htmlFor="message">
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project, timeline and goals."
                  className="form-input resize-y min-h-32"
                  data-testid="contact-message"
                />
              </FormField>

              <label className="flex items-start gap-3 border-t border-white/8 pt-5">
                <input
                  type="checkbox"
                  name="privacy_accepted"
                  checked={form.privacy_accepted}
                  onChange={handleChange}
                  required
                  className="mt-1 h-4 w-4 shrink-0 accent-[#E9C947]"
                  data-testid="contact-privacy-accepted"
                />
                <span className="text-xs leading-relaxed text-brand-gray">
                  I acknowledge that I have read the{" "}
                  <Link
                    to="/privacy-policy"
                    className="link-underline text-brand-white hover:text-brand-yellow"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and understand that the information I submit will be processed to
                  respond to my enquiry.
                </span>
              </label>

              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-brand-gray max-w-md">
                  Your enquiry is saved in the Minga administrative system and a
                  notification is sent to {COMPANY.email}.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <Send className="h-4 w-4" />
                </button>
              </div>

              {submitted && (
                <div
                  className="mt-3 border-l-2 border-brand-green pl-4 py-3 bg-brand-green/5"
                  data-testid="contact-success"
                >
                  <p className="eyebrow text-brand-green">[ Message Received ]</p>
                  <p className="body-text mt-2">
                    Thank you for reaching out. We’ll respond within one business day.
                  </p>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.12);
          color: #FAFAFA;
          padding: 0.85rem 1rem;
          border-radius: 2px;
          font-family: 'Roboto','Inter',sans-serif;
          font-size: 0.95rem;
          transition: border-color .3s ease, background .3s ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #E9C947;
          background: rgba(255,255,255,0.05);
        }
        select.form-input {
          appearance: none;
          background-image: linear-gradient(45deg, transparent 50%, #E9C947 50%), linear-gradient(135deg, #E9C947 50%, transparent 50%);
          background-position: calc(100% - 20px) 55%, calc(100% - 14px) 55%;
          background-size: 6px 6px, 6px 6px;
          background-repeat: no-repeat;
          padding-right: 2.4rem;
        }
        select.form-input option { background: #111; color: #FAFAFA; }
      `}</style>
    </div>
  );
}

function FormField({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="eyebrow-muted block mb-2">{label}</span>
      {children}
    </label>
  );
}
